import React from "react";
import { Link } from "react-router-dom";
import { Search, Link2, MessageSquare, ArrowRight, Users } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { BridgeMark } from "../components/BridgeMotif";
import { EmergencyBanner } from "./LegalPages";
import { Button } from "../components/ui/button";

export default function Home() {
  const { t } = useLang();

  const steps = [
    { icon: Search, title: t("home.step1_title"), desc: t("home.step1_desc") },
    { icon: Link2, title: t("home.step2_title"), desc: t("home.step2_desc") },
    { icon: MessageSquare, title: t("home.step3_title"), desc: t("home.step3_desc") },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <EmergencyBanner />
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-16 sm:px-6 lg:px-8 lg:pt-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="animate-fade-up">
              <p className="text-xs font-semibold uppercase tracking-widest text-terracotta">conquienhoy.com</p>
              <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slateink sm:text-5xl lg:text-6xl">
                {t("brand.tagline")}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">{t("home.hero_sub")}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild size="lg" className="rounded-full bg-terracotta px-7 hover:bg-terracotta-hover" data-testid="hero-find-btn">
                  <Link to="/encontrar-apoyo">{t("home.cta_find")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-slateink/20 px-7" data-testid="hero-join-btn">
                  <Link to="/unirme">{t("home.cta_join")}</Link>
                </Button>
              </div>
            </div>
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 -z-10 rounded-full bg-terracotta-light/60 blur-3xl" />
              <div className="w-full max-w-md rounded-3xl border border-border bg-white/70 p-10 backdrop-blur">
                <div className="flex justify-center" data-testid="brand-mark-card">
                  <BridgeMark size={96} />
                </div>
                <p className="mt-6 text-center font-display text-sm italic text-muted-foreground">
                  {t("home.trust_desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slateink sm:text-3xl lg:text-4xl">{t("home.how_title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("home.how_sub")}</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} className="relative rounded-2xl border border-border bg-white p-8" data-testid={`home-step-${i}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-terracotta-light text-terracotta">
                <s.icon className="h-6 w-6" />
              </div>
              <span className="absolute right-6 top-6 font-display text-4xl font-bold text-secondary">{i + 1}</span>
              <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-slateink">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Initiatives */}
      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-border bg-slateink px-8 py-14 sm:px-14">
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-terracotta"><Users className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">{t("home.initiatives_title")}</span></div>
              <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">{t("home.initiatives_title")}</h2>
              <p className="mt-3 text-white/70">{t("home.initiatives_desc")}</p>
            </div>
            <Button asChild size="lg" className="rounded-full bg-terracotta px-7 hover:bg-terracotta-hover" data-testid="home-initiatives-btn">
              <Link to="/iniciativas">{t("home.initiatives_cta")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
